import { Card, PageHeader } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { AuthorizationErrorCode, CompensationPayoutLevel, PractitionerRole } from '@common/enums';
import { EmptyListPlaceholder } from '@frontend/components/elements/EmptyListPlaceholder';
import { getSelectedLanguage, translateKey } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';
import { hasPractitionerRole } from '@frontend/utils/hasPractitionerRole';
import { PayoutsDetails } from '../payments/PayoutsDetails';

export function OrganizationPayments() {
    const abortableFetch = useAbortableFetch();
    const translate = getSelectedLanguage();
    const { organizationId } = useParams() as { organizationId: string };
    const [organizationRelationNumber, setOrganizationRelationNumber] = useState<string>();
    const backLink = `/organizations/${organizationId}/view/organizationPayouts`;
    const isOrganizationPayoutManager = hasPractitionerRole(
        useSelector((state: IReduxState) => state.authentication).employmentPractitionerRoles,
        PractitionerRole.organizationPayoutManager
    );

    useEffect(() => {
        loadOrganizationRelationNumber();
    }, [organizationId]);

    if (!isOrganizationPayoutManager) {
        throw new Error(AuthorizationErrorCode.MISSING_ORGANIZATION_PAYOUT_MANAGER_ROLE);
    }

    return (
        <>
            {organizationRelationNumber ? (
                <PayoutsDetails
                    backUrl={backLink}
                    payoutLevel={CompensationPayoutLevel.organization}
                    payoutLevelEntityId={organizationId}
                    payoutLevelEntityRelationNumber={organizationRelationNumber}
                />
            ) : (
                <>
                    <PageHeader title={translate.finance.payments} />
                    <Card>
                        <Card.Body>
                            <EmptyListPlaceholder
                                doNotWrapInCard={true}
                                subtitle={translateKey('finance.emptyState')}
                            />
                        </Card.Body>
                    </Card>
                </>
            )}
        </>
    );

    async function loadOrganizationRelationNumber() {
        const url = `/api/organizations/${organizationId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOrganizationRelationNumber(result.json.relationNumber);
    }
}
